import React, { useEffect } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks.ts';
import {
  selectOrganizations,
  selectCurrentOrganization,
  setOrganizations,
  setCurrentOrganization,
} from '../store/organization/organizationSlice.ts';
import { listMyOrganizations, setDefaultOrganization, OrganizationWithRole } from '../api.ts';
import { api } from '../api.ts';

export const OrganizationSwitcher = () => {
  const dispatch = useAppDispatch();
  const organizations = useAppSelector(selectOrganizations);
  const currentOrganization = useAppSelector(selectCurrentOrganization);

  // Load organizations on mount
  useEffect(() => {
    const load = async () => {
      try {
        // Ensure the /user endpoint runs first to process any pending invites
        await api.get('/user');
      } catch (err) {
        // ignore; /user may already be populated or unauthenticated during early mount
      }

      try {
        const orgs = await listMyOrganizations();
        dispatch(setOrganizations(orgs));
      } catch (error: any) {
        console.error('Failed to load organizations:', error);
        // Mark organizations as loaded even if the fetch fails to avoid blocking navigation
        dispatch(setOrganizations([]));
      }
    };

    load();
  }, [dispatch]);

  const handleOrganizationChange = async (event: SelectChangeEvent<number>) => {
    const orgId = event.target.value as number;
    const selectedOrg = organizations.find((org: OrganizationWithRole) => org.id === orgId);

    if (selectedOrg) {
      dispatch(setCurrentOrganization(selectedOrg));

      // Optionally set as default on the backend
      try {
        await setDefaultOrganization(orgId);
      } catch (error) {
        console.error('Failed to set default organization:', error);
      }

      // Reload the page to refresh data for the new organization
      window.location.reload();
    }
  };

  if (organizations.length === 0) {
    return null; // Don't show if user has no organizations
  }

  return (
    <Box sx={{ minWidth: 200, mr: 2 }}>
      <FormControl fullWidth size="small">
        <InputLabel
          id="organization-select-label"
          sx={{ color: 'white', '&.Mui-focused': { color: 'white' } }}
        >
          Organization
        </InputLabel>
        <Select
          labelId="organization-select-label"
          id="organization-select"
          value={currentOrganization?.id || ''}
          label="Organization"
          onChange={handleOrganizationChange}
          sx={{
            color: 'white',
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.7)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white',
            },
            '.MuiSvgIcon-root': {
              color: 'white',
            },
          }}
        >
          {organizations.map((org: OrganizationWithRole) => (
            <MenuItem key={org.id} value={org.id}>
              {org.name} {currentOrganization?.id === org.id && '(Active)'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};
